<template>
  <FormKit
    type="form"
    id="theFormId"
    :value="distributorReportForFormKit"
    v-model="distributorReportForFormKit"
    @submit="handleSubmit"
    :actions="false"
    autocomplete="off"
  >
    <template #default="{ state: formState }">
      <div class="pb-5 sm:flex-1 sm:overflow-auto">
        <FormSection v-if="distributorReportForFormKit">
          <template #sectionName>Review Request Details</template>
          <template #sectionDescription>
            <p>If you need the distributor to modify their distributor report, request a report review.</p>
            <br />
            <p>
              The distributor will be notified, and this distributor report will be sent back to draft so that it can be
              modified and submitted again by the distributor.
            </p>
          </template>
          <template #default>
            <FormKit
              label="Review Reason"
              name="reviewRequestReason"
              type="textarea"
              validation="required:trim|length:0,255"
            />
          </template>
        </FormSection>
      </div>
      <FormActions
        :save-action="saveAction"
        :cancel-action="cancelAction"
        :form-state="formState"
        save-label="Submit"
      ></FormActions
    ></template>
  </FormKit>
</template>

<script lang="ts">
import { routeNames } from "@/routes";
import { FormKit, submitForm } from "@formkit/vue";
import { FormActions, FormSection } from "@modernary/mui/components/form";
import { apiSaveErrorHandler, useApi } from "@modernary/mui/utils/api";
import { routeCancelAction, routeSaveAction } from "@modernary/mui/utils/helpers";
import { defineComponent, ref, type Ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { getDistributorReportById, saveDistributorReport } from "./distributorReportApi";
import distributorReportService from "./distributorReportService";
import { fillLookupValuesByPropertyNames } from "@modernary/mui/components/lookup";
import { LE } from "@/constants/enums";
import type { DistributorReport, DistributorReportFormKit } from "./distributorReport";
import { useDistributorReportStore } from "./distributorReportStore";

export default defineComponent({
  name: "RequestReview",
  components: {
    FormSection,
    FormActions,
  },
  setup(props) {
    const router = useRouter();
    const distributorReportStore = useDistributorReportStore();

    const route = useRoute();

    const distributorReport = ref<DistributorReport>();

    const { exec: fetchDistributorReport } = useApi(getDistributorReportById, {
      responseAdapter: async (response) => {
        await distributorReportService.skinnyResponseAdapter(response);
        return response.data;
      },
    });
    fetchDistributorReport(route.params.id).then((result) => {
      distributorReport.value = result;
    });

    const { exec: execSaveDistributorReport } = useApi(saveDistributorReport, {
      responseAdapter: async (response) => {
        await distributorReportService.responseAdapter(response);
        return response.data;
      },
    });

    const handleSubmit = async (data: DistributorReport) => {
      let result: DistributorReport | undefined;
      return apiSaveErrorHandler(
        async () => {
          data.statusMurn = LE.ReportStatus.Draft;
        },
        async () => {
          result = await execSaveDistributorReport(data);
        },
        async () => {
          if (!result) return;
          distributorReportStore.fill(result);
          routeSaveAction({
            router,
            routeName: routeNames.distributorReport.view,
            params: { id: result.id },
            isNew: false,
          });
        },
      );
    };

    const saveAction = function () {
      return submitForm("theFormId");
    };

    const cancelAction = function () {
      routeCancelAction(router, routeNames.home);
    };

    return {
      handleSubmit,
      saveAction,
      cancelAction,
      distributorReportForFormKit: distributorReport as Ref<DistributorReportFormKit>,
    };
  },
});
</script>
